import React, { useState } from 'react';
import {
  BookOpen,
  Shield,
  Sparkles,
  Heart,
  ScrollText,
  CheckCircle2,
  XCircle,
  Lightbulb,
  ChevronDown,
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';

/**
 * Tone of each paragraph → styled differently so the user
 * can visually scan what's DO / DON'T / TIP / NEUTRAL.
 *
 * type values:
 *   'do'     → green check
 *   'dont'   → red X
 *   'tip'    → amber lightbulb
 *   'info'   → neutral / book
 */
const HALAJOT_SECTIONS = [
  {
    icon: Shield,
    title: { es: 'Obligación', en: 'Obligation', he: 'חיוב' },
    subtitle: {
      es: 'Cuándo y por qué vestirlo',
      en: 'When and why to wear it',
      he: 'מתי ולמה ללבוש',
    },
    accent: 'amber',
    summary: {
      es: 'Aunque no hay transgresión por no usarlo, cada momento sin tzitzit es una mitzvá perdida.',
      en: 'Though there is no transgression for not wearing it, every moment without tzitzit is a lost mitzvah.',
      he: 'אף שאין עבירה במי שאינו לובש, כל רגע בלי ציצית הוא מצווה שהוחמצה.',
    },
    items: {
      es: [
        {
          type: 'info',
          text: 'No hay transgresión por no usar tzitzit. Sin embargo, no está permitido descuidarla, pues no es menos que cualquier costumbre obligatoria que se debe cumplir en Am Israel, que contiene en sí el cumplimiento de una mitzvá de la Torá, y cada momento sin tzitzit implica perderla.',
          ref: 'Shulján Aruj 24:1 · Igrot Moshé 4:8 · Shulján Aruj Harav 20',
        },
        {
          type: 'dont',
          text: 'No debe caminar la persona más de dos metros sin tzitzit.',
          ref: 'Mishná Berurá 8:1',
        },
      ],
      en: [
        {
          type: 'info',
          text: 'There is no transgression for not wearing tzitzit. However, it is not permitted to neglect it, as it is no less than any obligatory custom to be fulfilled in Am Israel, which contains in itself the fulfillment of a Torah mitzvah, and every moment without tzitzit means losing it.',
          ref: 'Shuljan Aruj 24:1 · Igrot Moshe 4:8 · Shuljan Aruj Harav 20',
        },
        {
          type: 'dont',
          text: 'One should not walk more than two meters without tzitzit.',
          ref: 'Mishna Berura 8:1',
        },
      ],
      he: [
        {
          type: 'info',
          text: 'אין עבירה במי שאינו לובש ציצית. עם זאת, אסור להזניח את המצווה, שהרי אינה פחותה מכל מנהג מחייב בעם ישראל, ויש בה קיום מצווה מן התורה — וכל רגע בלי ציצית הוא הפסד המצווה.',
          ref: 'שולחן ערוך כד:א · אגרות משה ד:ח · שולחן ערוך הרב כ',
        },
        {
          type: 'dont',
          text: 'אין לאדם ללכת יותר מארבע אמות (כשני מטרים) בלי ציצית.',
          ref: 'משנה ברורה ח:א',
        },
      ],
    },
  },
  {
    icon: Heart,
    title: { es: 'Uso Diario', en: 'Daily Use', he: 'שימוש יומיומי' },
    subtitle: {
      es: 'La práctica correcta día a día',
      en: 'The correct day-to-day practice',
      he: 'ההנהגה הנכונה ביום-יום',
    },
    accent: 'cyan',
    summary: {
      es: 'Blanco, constante y desde niño: una mitzvá que te acompaña en cada actividad.',
      en: 'White, constant and from childhood: a mitzvah that accompanies you in every activity.',
      he: 'לבן, קבוע ומגיל צעיר: מצווה שמלווה אותך בכל פעילות.',
    },
    items: {
      es: [
        { type: 'do', text: 'Según todas las opiniones, lo mejor es usar un tzitzit blanco con hilos blancos.', ref: 'Mishná Berurá 9:16' },
        { type: 'tip', text: 'Es bueno y correcto dormir con tzitzit por la noche; si resulta difícil, puede quitárselo. Pero quien persiste recibirá bendición, ya que "según el esfuerzo es la recompensa".', ref: 'Yalkut Yosef, p.15' },
        { type: 'do', text: 'Es correcto educar a los niños a vestir tzitzit desde pequeños, para que se acostumbren y estén siempre rodeados de mitzvot a lo largo de su vida.', ref: 'Halajá Berurá, p.361' },
        { type: 'info', text: 'Se permite entrar al baño con tzitzit; quien quiera ser estricto puede cubrir los hilos y recibirá bendición.', ref: 'Halajá Berurá p.398' },
        { type: 'tip', text: 'Que los adultos procuren usar hilos hechos a mano; los niños pueden usar hilos hechos a máquina.', ref: '' },
      ],
      en: [
        { type: 'do', text: 'According to all opinions, the best is to use a white tzitzit with white threads.', ref: 'Mishna Berura 9:16' },
        { type: 'tip', text: 'It is good and correct to sleep with tzitzit at night; if it is difficult, one may remove it. But whoever persists will receive blessing, as "according to the effort is the reward".', ref: 'Yalkut Yosef, p.15' },
        { type: 'do', text: 'It is correct to educate children to wear tzitzit from a young age, so they get accustomed and are always surrounded by mitzvot throughout their lives.', ref: 'Halaja Berura, p.361' },
        { type: 'info', text: 'It is permitted to enter the bathroom with tzitzit; whoever wants to be stricter can cover the threads and will receive blessing.', ref: 'Halaja Berura p.398' },
        { type: 'tip', text: 'Adults should strive to use handmade threads; children may use machine-made threads.', ref: '' },
      ],
      he: [
        { type: 'do', text: 'לכל הדעות, עדיף ללבוש ציצית לבנה עם חוטים לבנים.', ref: 'משנה ברורה ט:טז' },
        { type: 'tip', text: 'טוב ונכון לישון עם ציצית בלילה; אם קשה, אפשר להסירה. אך המתמיד יתברך, שכן "לפום צערא אגרא".', ref: 'ילקוט יוסף, עמ׳ 15' },
        { type: 'do', text: 'נכון לחנך את הילדים ללבוש ציצית מגיל צעיר, כדי שיתרגלו ויהיו מוקפים במצוות כל חייהם.', ref: 'הלכה ברורה, עמ׳ 361' },
        { type: 'info', text: 'מותר להיכנס לשירותים עם ציצית; הרוצה להחמיר יכול לכסות את החוטים ותבוא עליו ברכה.', ref: 'הלכה ברורה עמ׳ 398' },
        { type: 'tip', text: 'מבוגרים ישתדלו להשתמש בחוטי עבודת יד; ילדים יכולים להשתמש בחוטי מכונה.', ref: '' },
      ],
    },
  },
  {
    icon: Sparkles,
    title: { es: 'Bendición', en: 'Blessing', he: 'ברכה' },
    subtitle: {
      es: 'Cómo y cuándo se recita',
      en: 'How and when it is recited',
      he: 'איך ומתי מברכים',
    },
    accent: 'violet',
    summary: {
      es: 'La bendición se dice sobre el talit gadol en Shajrit, con intención de cubrir al katan.',
      en: 'The blessing is said over the talit gadol in Shajarit, with intention to cover the katan.',
      he: 'הברכה נאמרת על הטלית גדול בשחרית, בכוונה לפטור את הטלית קטן.',
    },
    items: {
      es: [
        { type: 'info', text: 'Se recita la bendición de tzitzit únicamente sobre el talit gadol en Shajrit, teniendo la intención de cumplir la mitzvá con ambas prendas.', ref: 'Mishná Berurá 8:24' },
        { type: 'tip', text: 'La bendición de Shehejeianu se recita solo sobre el talit gadol; sin embargo, si el talit katan le produce gran alegría, también puede recitarla.', ref: 'Halajá Berurá, p.406' },
      ],
      en: [
        { type: 'info', text: 'The blessing of tzitzit is recited only over the talit gadol in Shajarit, with the intention of fulfilling the mitzvah with both garments.', ref: 'Mishna Berura 8:24' },
        { type: 'tip', text: 'The blessing of Shehejeianu is recited only over the talit gadol; however, if the talit katan brings great joy, one may also recite it.', ref: 'Halaja Berura, p.406' },
      ],
      he: [
        { type: 'info', text: 'מברכים על הציצית רק על הטלית גדול בשחרית, בכוונה לצאת ידי חובה בשני הבגדים.', ref: 'משנה ברורה ח:כד' },
        { type: 'tip', text: 'ברכת שהחיינו נאמרת רק על הטלית גדול; אולם אם הטלית קטן מביאה לו שמחה גדולה, רשאי לברך גם עליה.', ref: 'הלכה ברורה, עמ׳ 406' },
      ],
    },
  },
  {
    icon: ScrollText,
    title: { es: 'Cuidado y Validez', en: 'Care & Validity', he: 'טיפול וכשרות' },
    subtitle: {
      es: 'Cómo mantenerlo kasher',
      en: 'How to keep it kosher',
      he: 'איך לשמור עליה כשרה',
    },
    accent: 'emerald',
    summary: {
      es: 'Hilos separados, respeto en Shabat y guenizá al desecharlo: lo básico para que no pierda validez.',
      en: 'Separated threads, Shabbat respect and geniza for disposal: the basics so it doesn\'t lose validity.',
      he: 'חוטים מופרדים, כבוד בשבת וגניזה בסיום: הבסיס כדי שלא תיפסל.',
    },
    items: {
      es: [
        { type: 'do', text: 'Es necesario mantener los hilos separados, bonitos y desenredados. Si están enredados, no invalida el tzitzit, a menos que el enredo sea tan severo que dificulte su separación.', ref: 'Piskei Teshuvot, p.89 · Mishná Berurá 8:18' },
        { type: 'dont', text: 'En Shabat no se deben apretar los nudos y es preferible abstenerse de desenredar los hilos.', ref: 'Jazón Ovadia, t.5, p.65 · Rav Yosi Tawil' },
        { type: 'tip', text: 'Si se corta uno de los ocho hilos por debajo de los nudos, el tzitzit permanece válido; si se cortan varios, se debe consultar a un rabino.', ref: '' },
        { type: 'info', text: 'Si se rompe o se invalida el tzitzit, no es necesario retirarlo de inmediato; por dignidad. Puede esperar y cambiarlo en privado.', ref: 'Mishná Berurá 13:11' },
        { type: 'dont', text: 'Desecho: no tirarlos a la basura; colocarlos con respeto para su recolección o, preferiblemente, en guenizá; quien lo hace recibirá bendición.', ref: 'Ramá 21:1' },
      ],
      en: [
        { type: 'do', text: 'It is necessary to keep the threads separated, beautiful, and untangled. If tangled, it does not invalidate the tzitzit, unless the tangle is so severe that it makes separation difficult.', ref: 'Piskei Teshuvot, p.89 · Mishna Berura 8:18' },
        { type: 'dont', text: 'On Shabbat one should not tighten the knots and it is preferable to abstain from untangling the threads.', ref: 'Jazon Ovadia, t.5, p.65 · Rav Yosi Tawil' },
        { type: 'tip', text: 'If one of the eight threads is cut below the knots, the tzitzit remains valid; if several are cut, a rabbi should be consulted.', ref: '' },
        { type: 'info', text: 'If the tzitzit breaks or becomes invalid, it is not necessary to remove it immediately; out of dignity. One may wait and change it in private.', ref: 'Mishna Berura 13:11' },
        { type: 'dont', text: 'Disposal: do not throw them in the trash; place them with respect for collection or, preferably, in geniza; whoever does so will receive blessing.', ref: 'Rama 21:1' },
      ],
      he: [
        { type: 'do', text: 'יש לשמור על החוטים מופרדים, נאים ולא מסובכים. אם הסתבכו — אין זה פוסל את הציצית, אלא אם ההסתבכות חמורה עד שקשה להפרידם.', ref: 'פסקי תשובות, עמ׳ 89 · משנה ברורה ח:יח' },
        { type: 'dont', text: 'בשבת אין להדק את הקשרים ועדיף להימנע מהפרדת החוטים.', ref: 'חזון עובדיה, ח״ה, עמ׳ 65 · הרב יוסי טוויל' },
        { type: 'tip', text: 'אם נחתך אחד משמונת החוטים מתחת לקשרים — הציצית כשרה; אם נחתכו כמה, יש לשאול רב.', ref: '' },
        { type: 'info', text: 'אם נקרעה או נפסלה הציצית, אין חובה להסירה מיד — משום כבוד הבריות. אפשר להמתין ולהחליפה בצנעה.', ref: 'משנה ברורה יג:יא' },
        { type: 'dont', text: 'גניזה: אין לזרוק לפח; יש להניחם בכבוד לאיסוף, ועדיף בגניזה; והעושה כן תבוא עליו ברכה.', ref: 'רמ״א כא:א' },
      ],
    },
  },
];

// Map accent name → tailwind classes. Tailwind can't infer dynamic class strings,
// so we list them fully here for the JIT.
const ACCENT = {
  amber: {
    bar: 'bg-gradient-to-r from-amber-500 to-orange-600',
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    chipDark: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    ring: 'ring-amber-400/30',
    dot: 'bg-amber-400',
    softBg: 'from-amber-50 to-orange-50/40',
  },
  cyan: {
    bar: 'bg-gradient-to-r from-cyan-500 to-blue-600',
    chip: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    chipDark: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/30',
    ring: 'ring-cyan-400/30',
    dot: 'bg-cyan-400',
    softBg: 'from-cyan-50 to-blue-50/40',
  },
  violet: {
    bar: 'bg-gradient-to-r from-violet-500 to-purple-600',
    chip: 'bg-violet-50 text-violet-700 border-violet-200',
    chipDark: 'bg-violet-500/15 text-violet-300 border-violet-400/30',
    ring: 'ring-violet-400/30',
    dot: 'bg-violet-400',
    softBg: 'from-violet-50 to-purple-50/40',
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-500 to-green-600',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    chipDark: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
    ring: 'ring-emerald-400/30',
    dot: 'bg-emerald-400',
    softBg: 'from-emerald-50 to-green-50/40',
  },
};

const ItemBadge = ({ type, language }) => {
  const label = {
    do: { es: 'Sí', en: 'Do', he: 'כן' },
    dont: { es: 'No', en: 'Don\'t', he: 'לא' },
    tip: { es: 'Tip', en: 'Tip', he: 'טיפ' },
    info: { es: 'Info', en: 'Info', he: 'מידע' },
  }[type];

  const styles = {
    do: { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    dont: { icon: XCircle, cls: 'bg-red-50 text-red-700 ring-red-200' },
    tip: { icon: Lightbulb, cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
    info: { icon: BookOpen, cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  }[type];
  const Icon = styles.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ring-1 ${styles.cls} flex-shrink-0`}
    >
      <Icon className="w-3 h-3" />
      {label[language] || label.es}
    </span>
  );
};

const TeachingsSection = () => {
  const { language } = useLanguage();
  const [expandedSection, setExpandedSection] = useState(0);

  const toggleSection = (idx) =>
    setExpandedSection(expandedSection === idx ? null : idx);

  return (
    <section
      id="halajot"
      className="py-12 md:py-20 bg-gradient-to-b from-slate-50 via-white to-slate-50"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-slate-900 rounded-full px-5 py-2 mb-5">
            <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-white text-xs font-bold uppercase tracking-widest">
              {tx(language, 'Guía Práctica', 'Practical Guide', 'מדריך מעשי')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-3 px-2">
            {tx(language, 'Guía Práctica de Halajot', 'Practical Halakhot Guide', 'מדריך הלכות מעשי')}
          </h2>
          <p className="text-sm sm:text-base text-slate-500 max-w-2xl mx-auto px-2">
            {tx(language, 'Aprende las 4 áreas clave para vivir la mitzvá del tzitzit correctamente, paso a paso.', 'Learn the 4 key areas to properly live the mitzvah of tzitzit, step by step.', 'למדו את 4 התחומים המרכזיים לחיות את מצוות הציצית יום-יום')}
          </p>
        </div>

        {/* Chapter chips — didactic table of contents */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-8">
          {HALAJOT_SECTIONS.map((sec, idx) => {
            const Icon = sec.icon;
            const accent = ACCENT[sec.accent];
            const isActive = expandedSection === idx;
            return (
              <button
                key={`chip-${idx}`}
                onClick={() => toggleSection(idx)}
                className={`group relative text-left rounded-xl p-3 sm:p-4 border-2 transition-all overflow-hidden ${
                  isActive
                    ? 'bg-white border-slate-900 shadow-lg'
                    : 'bg-white border-slate-100 hover:border-slate-200 hover:shadow-md'
                }`}
                data-testid={`halaja-chip-${idx}`}
              >
                {/* Left color bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${accent.bar}`} />
                <div className="flex items-center gap-2 sm:gap-3 mb-1.5 pl-1">
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? accent.bar : 'bg-slate-100'
                    }`}
                  >
                    <Icon
                      className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                        isActive ? 'text-white' : 'text-slate-500'
                      }`}
                    />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="pl-1 text-sm sm:text-base font-bold text-slate-900 leading-tight mb-0.5">
                  {sec.title[language] || sec.title.es}
                </h3>
                <p className="pl-1 text-[11px] sm:text-xs text-slate-500 leading-snug">
                  {sec.subtitle[language] || sec.subtitle.es}
                </p>
              </button>
            );
          })}
        </div>

        {/* Accordion cards */}
        <div className="space-y-3 sm:space-y-4">
          {HALAJOT_SECTIONS.map((sec, idx) => {
            const Icon = sec.icon;
            const accent = ACCENT[sec.accent];
            const isExpanded = expandedSection === idx;
            const items = sec.items[language] || sec.items.es;

            return (
              <div
                key={sec.title.es}
                className={`rounded-2xl border bg-white overflow-hidden transition-all duration-300 ${
                  isExpanded
                    ? `border-slate-200 shadow-xl ring-1 ${accent.ring}`
                    : 'border-slate-100 hover:border-slate-200 shadow-sm'
                }`}
              >
                <button
                  onClick={() => toggleSection(idx)}
                  className="w-full text-left p-4 sm:p-6 flex items-center gap-3 sm:gap-5"
                  data-testid={`halaja-section-${idx}`}
                >
                  <div
                    className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accent.bar} shadow-lg`}
                  >
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        {tx(language, 'Capítulo', 'Chapter', 'פרק')} {idx + 1}
                      </span>
                      <span className={`inline-block w-1 h-1 rounded-full ${accent.dot}`} />
                      <span className="text-[10px] font-semibold text-slate-500">
                        {items.length} {tx(language, 'puntos', 'points', 'נקודות')}
                      </span>
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                      {sec.title[language] || sec.title.es}
                    </h3>
                    {!isExpanded && (
                      <p className="text-xs sm:text-sm text-slate-500 mt-1 leading-snug line-clamp-2">
                        {sec.summary[language] || sec.summary.es}
                      </p>
                    )}
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="px-4 sm:px-6 pb-5 sm:pb-7">
                    {/* Key insight */}
                    <div
                      className={`relative rounded-xl p-3 sm:p-4 mb-5 bg-gradient-to-br ${accent.softBg} border border-slate-100`}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                        {tx(language, 'Idea clave', 'Key idea', 'רעיון מרכזי')}
                      </p>
                      <p className="text-sm sm:text-[15px] text-slate-800 leading-relaxed font-medium">
                        {sec.summary[language] || sec.summary.es}
                      </p>
                    </div>

                    {/* Items */}
                    <ol className="space-y-4">
                      {items.map((p, pIdx) => (
                        <li
                          key={pIdx}
                          className="flex gap-3 sm:gap-4 items-start"
                        >
                          {/* Number */}
                          <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-900 text-white text-[11px] sm:text-xs font-bold flex items-center justify-center">
                              {pIdx + 1}
                            </div>
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <ItemBadge type={p.type} language={language} />
                            </div>
                            <p className="text-slate-700 text-sm sm:text-[15px] leading-relaxed">
                              {p.text}
                            </p>
                            {p.ref && (
                              <div className="flex items-start gap-2 mt-2">
                                <div className={`w-5 h-px mt-2 ${accent.bar} opacity-60 flex-shrink-0`} />
                                <p className="text-slate-500 text-[11px] sm:text-xs font-medium tracking-wide italic">
                                  {p.ref}
                                </p>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer legend */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[11px]">
          <span className="text-slate-500 font-semibold">
            {tx(language, 'Leyenda:', 'Legend:', 'מקרא:')}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> {tx(language, 'Sí — Recomendado', 'Do — Recommended', 'כן — מומלץ')}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 ring-1 ring-red-200">
            <XCircle className="w-3 h-3" /> {tx(language, 'No — Prohibido', 'Don\'t — Forbidden', 'לא — אסור')}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            <Lightbulb className="w-3 h-3" /> {tx(language, 'Consejo', 'Tip', 'עצה')}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200">
            <BookOpen className="w-3 h-3" /> {tx(language, 'Halajá', 'Halakha', 'הלכה')}
          </span>
        </div>
      </div>
    </section>
  );
};

export default TeachingsSection;
